import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const STATUS_STYLES = {
  pending: 'bg-amber-100 text-amber-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  expired: 'bg-gray-100 text-gray-600',
};

const ApprovalDetailModal = ({ approval, onClose, onResend, onSendReminder }) => {
  if (!approval) return null;

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d)?.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const isOverdue = approval?.status === 'pending' && approval?.deadline_at && new Date(approval.deadline_at) < new Date();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="ClipboardCheck" size={18} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="font-semibold text-foreground">Approval Details</h2>
              <p className="text-xs text-muted-foreground">{approval?.reference ?? '—'}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-colors">
            <Icon name="X" size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Status Banner */}
          {isOverdue && (
            <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <Icon name="AlertTriangle" size={16} />
              <span className="font-medium">This approval is overdue</span>
            </div>
          )}

          {/* Info Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-muted-foreground font-medium mb-1">Type</p>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                approval?.type === 'ticket' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
              }`}>
                <Icon name={approval?.type === 'ticket' ? 'ClipboardList' : 'FileText'} size={11} />
                {approval?.type === 'ticket' ? 'Ticket' : 'Quote'}
              </span>
            </div>
            <div>
              <p className="text-xs text-muted-foreground font-medium mb-1">Status</p>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLES?.[approval?.status] ?? STATUS_STYLES?.expired}`}>
                {approval?.status?.charAt(0)?.toUpperCase() + approval?.status?.slice(1)}
              </span>
            </div>
            <div>
              <p className="text-xs text-muted-foreground font-medium mb-1">Customer</p>
              <p className="text-sm font-medium text-foreground">{approval?.customer_name ?? '—'}</p>
              {approval?.customer_email && <p className="text-xs text-muted-foreground">{approval?.customer_email}</p>}
            </div>
            <div>
              <p className="text-xs text-muted-foreground font-medium mb-1">Reference</p>
              <p className="text-sm font-mono text-foreground">{approval?.reference ?? '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground font-medium mb-1">Sent Date</p>
              <p className="text-sm text-foreground">{formatDate(approval?.created_at)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground font-medium mb-1">Deadline</p>
              <p className={`text-sm font-medium ${isOverdue ? 'text-red-600' : 'text-foreground'}`}>
                {formatDate(approval?.deadline_at)}
              </p>
            </div>
            {approval?.decided_at && (
              <div>
                <p className="text-xs text-muted-foreground font-medium mb-1">Decision Date</p>
                <p className="text-sm text-foreground">{formatDate(approval?.decided_at)}</p>
              </div>
            )}
            {approval?.customer_signature_name && (
              <div>
                <p className="text-xs text-muted-foreground font-medium mb-1">Signed By</p>
                <p className="text-sm text-foreground">{approval?.customer_signature_name}</p>
              </div>
            )}
          </div>

          {/* Notes */}
          {(approval?.notes || approval?.customer_notes) && (
            <div className="space-y-3">
              {approval?.notes && (
                <div className="bg-muted/30 rounded-lg p-3">
                  <p className="text-xs font-medium text-muted-foreground mb-1">Internal Notes</p>
                  <p className="text-sm text-foreground">{approval?.notes}</p>
                </div>
              )}
              {approval?.customer_notes && (
                <div className="bg-muted/30 rounded-lg p-3">
                  <p className="text-xs font-medium text-muted-foreground mb-1">Customer Notes</p>
                  <p className="text-sm text-foreground">{approval?.customer_notes}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-border bg-muted/20">
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
          {approval?.status === 'pending' && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { onSendReminder?.(approval); onClose(); }}
              >
                <Icon name="Bell" size={14} className="mr-1" /> Send Reminder
              </Button>
              <Button
                size="sm"
                onClick={() => { onResend?.(approval); onClose(); }}
              >
                <Icon name="Send" size={14} className="mr-1" /> Resend Approval
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApprovalDetailModal;
